import React from 'react';
import { Lock } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { VisibilityBars } from './VisibilityBars';
import { getRowColorClasses } from './rowColors';
import { getMoonPhaseEmoji } from '@/utils/moonCalculations';
import { MOAB_MARCH_2026 } from './calendarPreviewSample';
import { HERO_PREMIUM_COPY } from '@/components/heroPremiumCopy';

const th = 'h-auto px-1 py-2 text-xs font-medium leading-tight';
const td = 'px-1 py-2';

const LOCKED_LEAD = 'Moon Planner';

interface CalendarPreviewSliceProps {
  /** Locked Moon Planner row. The home hero shows it; /features hides it. */
  showPremiumRow?: boolean;
  /** Sample nights to show, from the start of MOAB_MARCH_2026. Defaults to every row. */
  maxRows?: number;
}

export const CalendarPreviewSlice: React.FC<CalendarPreviewSliceProps> = ({
  showPremiumRow = true,
  maxRows,
}) => {
  const nights = maxRows == null ? MOAB_MARCH_2026 : MOAB_MARCH_2026.slice(0, maxRows);
  return (
    <div className="rounded-xl border border-border bg-card shadow-lg overflow-hidden">
    <Table>
      <TableHeader>
        <TableRow className="border-border/50 bg-secondary/20 hover:bg-secondary/20">
          <TableHead className={`${th} text-foreground`}>{HERO_PREMIUM_COPY.date}</TableHead>
          <TableHead className={`${th} text-space-gray`}><span aria-hidden="true">✨ </span>{HERO_PREMIUM_COPY.coreStart}</TableHead>
          <TableHead className={`${th} text-space-gray`}><span aria-hidden="true">✨ </span>{HERO_PREMIUM_COPY.coreEnd}</TableHead>
          <TableHead className={`${th} text-muted-foreground text-center`}>{HERO_PREMIUM_COPY.moonPercent}</TableHead>
          <TableHead className={`${th} text-muted-foreground text-center`}>{HERO_PREMIUM_COPY.phase}</TableHead>
          <TableHead className={`${th} text-galaxy-blue text-center`}>{HERO_PREMIUM_COPY.visibility}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {nights.map((d) => {
          const c = getRowColorClasses(d.rating);
          return (
            <TableRow key={d.label} className={`border-border/30 hover:bg-transparent ${c.row}`}>
              <TableCell className={`${td} text-foreground font-medium border-r border-border/30 whitespace-nowrap ${c.sticky} ${c.border}`}>
                <div className="text-sm">{d.label}</div>
              </TableCell>
              <TableCell className={`${td} text-space-gray font-medium text-xs whitespace-nowrap`}>{d.gcStart}</TableCell>
              <TableCell className={`${td} text-space-gray font-medium text-xs whitespace-nowrap`}>{d.gcEnd}</TableCell>
              <TableCell className={`${td} text-muted-foreground text-center text-xs`}>{d.moonPercent}%</TableCell>
              <TableCell className={`${td} text-xl text-center leading-none`}>{getMoonPhaseEmoji(d.moonPhase, d.moonPhaseCycle)}</TableCell>
              <TableCell className={`${td} text-center border-l border-border/30 ${c.sticky}`}><VisibilityBars rating={d.rating} /></TableCell>
            </TableRow>
          );
        })}
        {showPremiumRow && (
          <TableRow className="hover:bg-transparent border-0">
            <TableCell colSpan={6} className="px-3 py-2 bg-secondary/20">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Lock className="w-3.5 h-3.5 shrink-0 text-galaxy-blue" aria-hidden="true" />
                <span>
                  <span className="font-medium text-foreground">{LOCKED_LEAD}</span>
                  {HERO_PREMIUM_COPY.lockedRow.slice(LOCKED_LEAD.length)}
                </span>
                <span>{HERO_PREMIUM_COPY.lockedLabel}</span>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    </div>
  );
};
